import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { NotificationController } from 'core-components/library/popup'

import { checkCockpitReminder, sendCockpitReminder } from '../../../api'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../configs/translations'
import {
  buildAttentionReminderRequests,
  type IAttentionReminderList,
  type IAttentionReminderRecipient,
  type IAttentionReminderRoute,
  type TAttentionReminderScope,
} from '../../../lib'
import { markCockpitAttentionReminded } from '../../../model/financialCockpitSlice'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.reminder

/** The server's reason for a recipient reminded already today (`reminder/check`). */
const REMINDED_TODAY_REASON = 'Already reminded today'

/**
 * Why a reminder cannot go out now: everyone it reaches was reminded today, or it reaches no one
 * the service may write to.
 */
export type TAttentionReminderBlock = 'remindedToday' | 'noRecipient'

/** Where one person stands: may be reminded, was reminded today, may not be reminded, is being sent. */
export type TAttentionRecipientState = 'ready' | 'remindedToday' | 'blocked' | 'pending'

export interface IUseAttentionReminderParams {
  recipients: IAttentionReminderRecipient[]
  route: IAttentionReminderRoute
  scope: TAttentionReminderScope | undefined
  /** Partners of a list reminder; without it the reminder goes per partner the recipients carry. */
  list?: IAttentionReminderList
  /** `false` asks the service nothing, e.g. for a reminder the reader is not offered. */
  enabled?: boolean
}

export interface IAttentionReminder {
  /** Sends to everyone, or to the people given. */
  remind: (people?: IAttentionReminderRecipient[]) => Promise<void>
  /** Unknown until the check answers, and when it fails: the send then answers for itself. */
  stateOf: (email: string) => TAttentionRecipientState | undefined
  block: TAttentionReminderBlock | null
  isPending: boolean
  /** The service has not answered yet whether anyone can be reminded. */
  isChecking: boolean
}

interface IReminderAnswer {
  email: string
  status: string
  reason?: string
}

const toState = (answers: IReminderAnswer[]): TAttentionRecipientState => {
  if (answers.some(({ status }) => status === 'ready')) return 'ready'

  return answers.some(({ reason }) => reason === REMINDED_TODAY_REASON)
    ? 'remindedToday'
    : 'blocked'
}

/** Where each person stands by the service's answers, over every partner they were asked about. */
const toStates = (answers: IReminderAnswer[]): Record<string, TAttentionRecipientState> => {
  const byEmail = new Map<string, IReminderAnswer[]>()
  answers.forEach((answer) => {
    const email = answer.email.toLowerCase()
    byEmail.set(email, [...(byEmail.get(email) ?? []), answer])
  })

  return Object.fromEntries([...byEmail].map(([email, own]) => [email, toState(own)]))
}

/**
 * A send's answer read as a check's: `sent` is reminded today (every reminder type allows one a
 * day), `failed` may be tried again, `skipped` carries the reason a check would give.
 */
const fromSendAnswer = (answer: IReminderAnswer): IReminderAnswer => {
  if (answer.status === 'sent')
    return { ...answer, status: 'skipped', reason: REMINDED_TODAY_REASON }

  return answer.status === 'failed' ? { ...answer, status: 'ready' } : answer
}

/**
 * One cockpit reminder: who, about which status and which partners. It asks `reminder/check` per
 * person once, before anything is clicked, and sends through `reminder/send` with the type
 * `buildAttentionReminderRequests` picks; the send's own per-person answer then updates the state,
 * so nothing is asked again. A send that fails leaves the state as it was. Every Remind button of the cockpit but the
 * acknowledgment signers' goes through here.
 */
export const useAttentionReminder = ({
  recipients,
  route,
  scope,
  list,
  enabled = true,
}: IUseAttentionReminderParams): IAttentionReminder => {
  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const [states, setStates] = useState<Record<string, TAttentionRecipientState>>({})
  const [pendingEmails, setPendingEmails] = useState<string[]>([])
  /* The requests the check last answered, failed included. Until it is these, the check is still
     out, from the first render on rather than from the effect that starts it. */
  const [answeredKey, setAnsweredKey] = useState<string | null>(null)

  const key = JSON.stringify([recipients, route, scope, list])
  const requests = useMemo(
    () => buildAttentionReminderRequests(recipients, route, scope, list),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the key stands for the inputs
    [key]
  )

  useEffect(() => {
    setStates({})

    if (!enabled || requests.length === 0) {
      return
    }

    let isCurrent = true

    void Promise.allSettled(requests.map((request) => checkCockpitReminder(request))).then(
      (settled) => {
        if (!isCurrent) return

        setAnsweredKey(key)
        if (settled.some(({ status }) => status === 'rejected')) return

        setStates(
          toStates(settled.flatMap((result) => (result.status === 'fulfilled' ? result.value : [])))
        )
      }
    )

    return () => {
      isCurrent = false
    }
  }, [enabled, key, requests])

  const remind = useCallback(
    async (people: IAttentionReminderRecipient[] = recipients) => {
      const toSend = buildAttentionReminderRequests(people, route, scope, list)

      if (toSend.length === 0) {
        NotificationController.showWarning({ message: t(KEYS.unavailable) })
        return
      }

      const emails = people.map(({ email }) => email)
      setPendingEmails((current) => [...current, ...emails])

      try {
        const settled = await Promise.allSettled(
          toSend.map((request) => sendCockpitReminder(request))
        )
        const answers = settled.flatMap((result) =>
          result.status === 'fulfilled' ? result.value : []
        )
        const sent = answers.filter(({ status }) => status === 'sent').map(({ email }) => email)

        setStates((current) => ({ ...current, ...toStates(answers.map(fromSendAnswer)) }))

        if (sent.length === 0) {
          NotificationController.showError({ message: t(KEYS.failure) })
          return
        }

        dispatch(markCockpitAttentionReminded(sent))
        NotificationController.showSuccess({ message: t(KEYS.success) })
      } finally {
        setPendingEmails((current) => current.filter((email) => !emails.includes(email)))
      }
    },
    [dispatch, list, recipients, route, scope, t]
  )

  const stateOf = useCallback(
    (email: string) => (pendingEmails.includes(email) ? 'pending' : states[email.toLowerCase()]),
    [pendingEmails, states]
  )

  const block = useMemo<TAttentionReminderBlock | null>(() => {
    if (!enabled) return null
    if (!scope || requests.length === 0) return 'noRecipient'

    const known = recipients.map(({ email }) => states[email.toLowerCase()])
    if (known.some((state) => state === undefined || state === 'ready')) return null

    return known.includes('remindedToday') ? 'remindedToday' : 'noRecipient'
  }, [enabled, recipients, requests.length, scope, states])

  const isChecking = enabled && requests.length > 0 && answeredKey !== key

  return { remind, stateOf, block, isPending: pendingEmails.length > 0, isChecking }
}
